// `POST /rounds/session` — the one route a rounds-mode browser can reach with nothing in hand
// (rounds-slice implementation-review **F1**, against the server's **F2** credential model).
//
// A `fetch` client in the `lobby-http.ts` / `rounds-http.ts` mould, and nothing more. Three things
// about the contract shape the code:
//
//   * **The credential is the identity.** A display name is a label; presenting one selects
//     nothing. Present a credential and you return to that membership; present the tournament's
//     **join code** and a new membership is minted. So the client's job is to keep the credential
//     and always offer it.
//   * **A refused credential is never silently replaced.** `403 unknown_credential` /
//     `400 invalid_credential` are distinct outcomes, not "try again fresh": deciding to abandon a
//     membership is a decision the caller makes ({@link restoreRoundsSession}) and records, so it
//     can never happen invisibly on a transport hiccup.
//   * **There is no credential-free mint** (`home-screen` **D-c**). `mint_handler` refuses
//     `unauthorized` unless the caller presents a join code or an issued credential, so a boot that
//     minted with neither was requesting a refusal — which is precisely the defect that made rounds
//     mode unreachable. A mint now happens on exactly two occasions: a **restore** (a stored
//     credential) and a **join** (a code the player typed on Home).

import type { RoundsCredentialStore } from '../app/rounds/rounds-credential-store';

/** What kind of member a credential resolves to (`MemberKind::as_str`). `spectator` is the one
 * that changes what the client may offer: minted after membership closed, it carries no sandbox,
 * no submission and no rank. */
export type RoundsMemberKind = 'host' | 'competitor' | 'spectator';

/** The minted membership: a bearer token scoped to one membership, plus the credential to keep. */
export interface RoundsSession {
  /** The bearer token every other rounds route authorizes with. */
  token: string;
  /** The member's sandbox scope in the tournament's current round, `sandbox-r{round}-p{player}` —
   * **`null` for a spectator**, who has none. The server's `MintedSessionDto.sandbox` is an
   * `Option<String>` for exactly this reason, and a `string`-only shape here used to turn a
   * legitimate spectator mint into a spurious `unavailable`. */
  sandbox: string | null;
  displayName: string;
  /** The 256-bit tournament credential — the only way back here. Persist it. */
  credential: string;
  /** Whether this member competes. Read by Home so a spectator join is never presented as a
   * playing one (`client-home`). Defaults to `competitor` on a surface that names none. */
  memberKind: RoundsMemberKind;
  /** The tournament this membership belongs to; empty on a surface that names none. */
  tournamentId: string;
  /** The member's join ordinal — 0 is the host's. */
  joinOrdinal: number;
  /** The tournament's current round at mint time. */
  round: number;
}

/** One mint attempt's outcome. Typed refusals are values, never exceptions (the `rounds-http`
 * convention), because a silent no-op at boot is the one thing that must not happen.
 *
 * The refusal set is deliberately **wider than one generic failure**: Home renders each beside the
 * field that caused it, and `client-home` requires a wrong code and a full roster to read
 * differently — they sit on opposite sides of an authentication boundary (design **D-d**). */
export type MintOutcome =
  | { kind: 'minted'; session: RoundsSession }
  /** `403 unauthorized` — no tournament exists, or the join code is not the one it was issued
   * with. Deliberately one outcome for both: the server may not disclose which. */
  | { kind: 'unauthorized' }
  /** `409 roster_full` — the code was accepted and every colony slot is taken. Reachable only
   * *with* a valid code, so naming it discloses nothing new. */
  | { kind: 'rosterFull' }
  /** `403` — the server never issued the credential we presented. */
  | { kind: 'unknownCredential' }
  /** `400` — the credential we presented is not well-formed hex. */
  | { kind: 'invalidCredential' }
  /** `404` — this server mounts no rounds surface (it is a live deployment). */
  | { kind: 'notRounds' }
  /** Any other named refusal (`invalid_display_name`, `membership_unavailable`, …). */
  | { kind: 'refused'; status: number; error: string }
  /** The request never got an answer, or the answer was not the JSON it claimed. */
  | { kind: 'unavailable'; message: string };

export interface MintRoundsSessionConfig {
  /** The API origin; `''` (the default) is same-origin, which is what production uses. */
  baseUrl?: string;
  fetchImpl?: typeof fetch;
  /** The player's chosen name, or **absent** under an authentication gate — see
   * {@link JoinRoundsSessionDeps.displayName}. */
  displayName?: string;
  /** The credential a previous mint issued, when we have one. */
  credential?: string | null;
  /** The tournament's join code, as the player typed or pasted it. Normalized before sending. */
  joinCode?: string | null;
}

/** The display name used when the player has never been asked for one. Home asks (**D18**), so
 * this is now only the floor under a blank field, not the name a boot mints under. */
export const DEFAULT_ROUNDS_DISPLAY_NAME = 'Beekeeper';

/** The longest name the surface accepts (`MAX_DISPLAY_NAME_CHARS`), clamped here so a long name
 * is trimmed locally rather than refused as `400 invalid_display_name`. */
const MAX_DISPLAY_NAME_CHARS = 64;

function normalizeDisplayName(name: string | undefined): string {
  const trimmed = (name ?? '').trim();
  const safe = trimmed.length === 0 ? DEFAULT_ROUNDS_DISPLAY_NAME : trimmed;
  return [...safe].slice(0, MAX_DISPLAY_NAME_CHARS).join('');
}

/**
 * The join code as the wire wants it: trimmed and case-folded.
 *
 * The server parses it with `TournamentSecret::from_hex` and matches on the parsed bytes, so
 * `AB…` and `ab…` are the same code — but a stray newline from a chat-app paste is not, and the
 * refusal for that is the same opaque `unauthorized` a genuinely wrong code gets. Normalizing here
 * is what stops the most common real failure from being indistinguishable from the security one.
 */
export function normalizeJoinCode(code: string): string {
  return code.trim().toLowerCase();
}

/** The `memberKind` a surface named, or `competitor` when it named none (a fake or an older
 * surface). Never a throw: the field is informational, and the credential is the identity. */
function memberKindOf(value: unknown): RoundsMemberKind {
  return value === 'host' || value === 'spectator' || value === 'competitor'
    ? value
    : 'competitor';
}

/** One mint attempt. */
export async function mintRoundsSession(config: MintRoundsSessionConfig): Promise<MintOutcome> {
  const baseUrl = (config.baseUrl ?? '').replace(/\/+$/, '');
  const fetchImpl = config.fetchImpl ?? globalThis.fetch.bind(globalThis);
  // **Omitted entirely when there is no name to send** (`entra-authentication`). The server's
  // `displayName` is conditionally required — required ungated, ignored under a gate — so sending
  // a placeholder here would put a value on the wire the server discards, and sending `null` would
  // fail `deny_unknown_fields`' sibling rule for an absent `Option`.
  const body: Record<string, unknown> = {};
  if (config.displayName !== undefined) {
    body.displayName = normalizeDisplayName(config.displayName);
  }
  // Omitted, never sent as `null`: the request DTO denies unknown fields and `Option<String>`
  // wants the key absent when there is nothing to present. True of both secrets.
  if (typeof config.credential === 'string' && config.credential.length > 0) {
    body.credential = config.credential;
  }
  if (typeof config.joinCode === 'string') {
    const code = normalizeJoinCode(config.joinCode);
    if (code.length > 0) {
      body.joinCode = code;
    }
  }

  let response: Response;
  try {
    response = await fetchImpl(`${baseUrl}/rounds/session`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
  } catch (error) {
    return { kind: 'unavailable', message: String((error as Error)?.message ?? error) };
  }

  let parsed: unknown = null;
  try {
    const text = await response.text();
    parsed = text.length === 0 ? null : JSON.parse(text);
  } catch {
    parsed = null;
  }
  const named = (parsed ?? {}) as Record<string, unknown>;
  const error = typeof named['error'] === 'string' ? (named['error'] as string) : null;

  if (!response.ok) {
    if (response.status === 404) {
      return { kind: 'notRounds' };
    }
    if (error === 'unknown_credential') {
      return { kind: 'unknownCredential' };
    }
    if (error === 'invalid_credential') {
      return { kind: 'invalidCredential' };
    }
    if (error === 'unauthorized') {
      return { kind: 'unauthorized' };
    }
    if (error === 'roster_full') {
      return { kind: 'rosterFull' };
    }
    return { kind: 'refused', status: response.status, error: error ?? `http_${response.status}` };
  }

  const token = named['token'];
  const sandbox = named['sandbox'];
  const credential = named['credential'];
  // `sandbox` is legitimately absent for a spectator, so only `token` and `credential` are
  // load-bearing for calling the answer a session at all.
  if (typeof token !== 'string' || typeof credential !== 'string') {
    return {
      kind: 'unavailable',
      message: 'the server minted a session but named no token/credential',
    };
  }
  return {
    kind: 'minted',
    session: {
      token,
      sandbox: typeof sandbox === 'string' ? sandbox : null,
      displayName:
        typeof named['displayName'] === 'string'
          ? (named['displayName'] as string)
          : normalizeDisplayName(config.displayName),
      credential,
      memberKind: memberKindOf(named['memberKind']),
      tournamentId: typeof named['tournamentId'] === 'string' ? named['tournamentId'] : '',
      joinOrdinal: typeof named['joinOrdinal'] === 'number' ? named['joinOrdinal'] : 0,
      round: typeof named['round'] === 'number' ? named['round'] : 0,
    },
  };
}

/** What a boot ends up with. */
export type RestoreRoundsSessionOutcome =
  | { kind: 'session'; session: RoundsSession }
  /** No membership to restore — either nothing was stored, or what was stored is definitively
   * refused. `discardedCredential` distinguishes the two, because the second costs the player a
   * membership and should say so. */
  | { kind: 'none'; discardedCredential: boolean }
  /**
   * The restore could not be decided — a thrown `fetch`, a timeout, or a `5xx` (including
   * `500 membership_unavailable`, which is how the server now reports a read it could not complete).
   *
   * **Nothing was refused, so nothing is claimed about the credential, which is kept.** This is the
   * only remaining outcome that keeps a credential the server did not accept, and it is the correct
   * one to do so: "ask again later" is not "not yours".
   */
  | { kind: 'failed'; outcome: MintOutcome }
  /** The caller stopped owning this restore while it was in flight — the player joined a *new*
   * membership from Home before the boot's re-mint answered. **Nothing was written**: the stored
   * credential is left exactly as the join left it. See {@link RestoreRoundsSessionDeps.isCurrent}. */
  | { kind: 'abandoned' };

export interface RestoreRoundsSessionDeps {
  baseUrl?: string;
  fetchImpl?: typeof fetch;
  credentials: RoundsCredentialStore;
  /**
   * Whether this restore still owns the credential store, consulted the instant before anything is
   * written.
   *
   * **This closes a real data-loss race, not a theoretical one** (implementation review). Home is
   * required to be interactive *while* the restore is in flight, so a player can join a new
   * membership before the boot's re-mint answers. Without this check the late restore would persist
   * the **old** credential over the new one and re-open the old membership — leaving the freshly
   * minted membership unrecoverable while it still occupies a roster slot, and a refused old
   * credential would `clear()` the new one outright.
   *
   * A synchronous predicate is sufficient and is the whole point: JavaScript cannot preempt between
   * this call and the write that follows it, so "check, then write" is atomic here in a way it
   * would not be in a threaded runtime.
   */
  isCurrent?: () => boolean;
}

/** Persists whatever a successful mint returned. A spectator has no sandbox scope; the record's
 * `sandbox` field is a display convenience and is never used to *select* a membership, so it
 * stores empty rather than blocking the credential — which is the only irreplaceable value here. */
function persist(credentials: RoundsCredentialStore, session: RoundsSession): void {
  credentials.set({
    credential: session.credential,
    displayName: session.displayName,
    sandbox: session.sandbox ?? '',
  });
}

/**
 * **Restore, and only restore** (`home-screen` **D-b** and **D-c**).
 *
 * The boot-time entry point: present the stored credential if there is one, and persist whatever
 * comes back. It **never** falls back to a credential-free mint — that request does not exist on
 * the shipped surface, so retrying it would be refused and would strand the boot on a screen with
 * nothing on it. A boot with no membership lands on Home in its no-membership state, which is a
 * usable screen rather than a dead end.
 *
 * A stored credential the server refuses (`unknown_credential` / `invalid_credential`) is cleared
 * and reported via `discardedCredential`, so a revoked membership never looks like an ordinary
 * first visit. Every other failure returns as-is with the credential kept: a transport hiccup must
 * never cost a player their membership.
 */
export async function restoreRoundsSession(
  deps: RestoreRoundsSessionDeps,
): Promise<RestoreRoundsSessionOutcome> {
  const stored = deps.credentials.get();
  if (stored === null) {
    return { kind: 'none', discardedCredential: false };
  }

  const restored = await mintRoundsSession({
    baseUrl: deps.baseUrl,
    fetchImpl: deps.fetchImpl,
    displayName: stored.displayName,
    credential: stored.credential,
  });

  // Ownership is checked **before either write below**, never after. Both are destructive to a
  // membership the player may have minted while this was in flight.
  if (deps.isCurrent?.() === false) {
    return { kind: 'abandoned' };
  }

  if (restored.kind === 'minted') {
    persist(deps.credentials, restored.session);
    return { kind: 'session', session: restored.session };
  }
  // Everything the server may answer a *presented credential* with, other than a mint or a 5xx, is a
  // definitive refusal (UI/UX audit **F-12**), so all three clear the credential:
  //
  //   * `unknownCredential` — never issued, or revoked.
  //   * `invalidCredential` — not well-formed hex; it can never be accepted.
  //   * `unauthorized` — no tournament is running, or the credential is not the one this tournament
  //     issued. Deliberately conflated by the server so a caller cannot probe whether a tournament
  //     exists; either way **this membership is unusable**.
  //
  // `unauthorized` used to be kept rather than cleared, behind a Retry/Forget prompt, because the
  // server folded transient database errors into the same `403` — so a blip was indistinguishable
  // from a revocation and clearing risked destroying a valid membership. Both of those folds are now
  // split server-side (`rounds_http`: `Err` → `500 membership_unavailable`), which is what makes
  // clearing safe and lets the boot stop re-presenting a credential the server will refuse again.
  if (restored.kind !== 'unknownCredential' && restored.kind !== 'invalidCredential' && restored.kind !== 'unauthorized') {
    return { kind: 'failed', outcome: restored };
  }
  // Refused, definitively. Forget it so it is never presented again.
  deps.credentials.clear();
  return { kind: 'none', discardedCredential: true };
}

export interface JoinRoundsSessionDeps {
  baseUrl?: string;
  fetchImpl?: typeof fetch;
  credentials: RoundsCredentialStore;
  /** The name the player chose on Home (**D18**) — a label in the standings, never an identity.
   *
   * **Absent under an authentication gate** (`entra-authentication`): the server derives the name
   * from the caller's principal and ignores any client-supplied value, so the field is omitted
   * from the request rather than sent to be discarded. */
  displayName?: string;
  /** The tournament's join code, as typed or pasted. */
  joinCode: string;
}

/**
 * **Join with a code the player supplied** — the second and only other place a mint happens.
 *
 * The returned credential is persisted **before** this resolves, because the caller's next act is
 * to navigate away from the screen holding it, and the credential is the only way back to the
 * membership this call just created. A refusal persists nothing.
 */
export async function joinRoundsSession(deps: JoinRoundsSessionDeps): Promise<MintOutcome> {
  const outcome = await mintRoundsSession({
    baseUrl: deps.baseUrl,
    fetchImpl: deps.fetchImpl,
    displayName: deps.displayName,
    joinCode: deps.joinCode,
  });
  if (outcome.kind === 'minted') {
    persist(deps.credentials, outcome.session);
  }
  return outcome;
}

/** A boot failure, in human words — the composition root renders this, never a status code.
 *
 * The **join-side** copy is not here: it is partitioned by what the server may disclose and
 * belongs beside the field that caused it ({@link joinRefusalCopy}). */
export function mintFailureMessage(outcome: MintOutcome): string {
  switch (outcome.kind) {
    case 'minted':
      return '';
    case 'notRounds':
      return 'This server is running a live match, not a practice round.';
    case 'unknownCredential':
    case 'invalidCredential':
      return 'That membership could not be reopened.';
    case 'unauthorized':
      return 'We couldn’t place you in a tournament with that code.';
    case 'rosterFull':
      return 'That tournament is full — every colony slot is taken.';
    case 'refused':
      return `The hive refused to open a membership (${outcome.error}).`;
    case 'unavailable':
      return `Couldn't reach the hive — ${outcome.message}`;
  }
}
